import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Divider
} from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import PropTypes from "prop-types";
import { Close } from "@mui/icons-material";
import { getCurrentTime } from "../utils/assistantUtils";
const cleanData = (data) => {
  if (Array.isArray(data)) {
    return data.map((value) => (value === "" ? null : cleanData(value)));
  }

  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, cleanData(value)]));
  }

  return data === "" ? null : data;
};

const AIA83Modal = ({ setAia83Intent, originatingRequestId, requestId, handleModuleSubmit, handleStop, setMessages, applicationNumbers }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [aia83Data, setAia83Data] = useState({
    application_nums: applicationNumbers || "",
    
    // Withdrawal Method
    withdrawalMethod: "allPractitioners", // allPractitioners, specificPractitioners, customerNumberPractitioners
    customerNumber: "",
    
    // 37 CFR Withdrawal Reasons
    cfrReasons: {
      "_11_116_a_1": false,
      "_11_116_a_2": false,
      "_11_116_a_3": false,
      "_11_116_b_7": false, // with Notes
      "_11_116_b_1": false,
      "_11_116_b_2": false,
      "_11_116_b_3": false,
      "_11_116_b_4": false,
      "_11_116_b_5": false,
      "_11_116_b_6": false,
      "_11_116_b_7_notes": ""
    },
    
    // Withdrawal Requirements Checklist
    withdrawalChecklist: {
      reasonableNotice: true,
      deliveredPapers: true,
      notifiedClient: true
    },
    
    // Explanation (if necessary)
    explanation: "",
    
    // Correspondence Address Change
    correspondenceChange: {
      changeAddress: false,
      method: "customerNumber", // customerNumber, applicant
      customerNumber: "",
      applicantAddress: {
        address: "",
        state: "",
        city: "",
        country: "",
        zipcode: "",
        telephone: "",
        email: ""
      }
    },
    
    // Signatory Information
    signatureRequired: true,
    signatoryInfo: {
      name: "",
      registrationNumber: "",
      addressLine1: "",
      state: "",
      city: "",
      country: "",
      zipcode: "",
      telephone: "",
      email: ""
    }
  });
  const [isRequiredField, setIsRequiredField] = useState(false);

  const scrollToLastFile = useRef(null);

  // Update application numbers when prop changes
  useEffect(() => {
    if (applicationNumbers) {
      setAia83Data(prev => ({
        ...prev,
        application_nums: applicationNumbers
      }));
    }
  }, [applicationNumbers]);

  useEffect(() => {
    setTimeout(() => {
      if (scrollToLastFile.current) {
        scrollToLastFile.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 1);
  }, [uploadedFiles]);

  const handleDelete = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
  };

  const handleSubmit = async () => {
    // Validate required fields
    
    // Check if at least one withdrawal method checkbox is selected
    if(!aia83Data.withdrawalMethod){
      setIsRequiredField(true);
      return;
    }


    // Validate customer number if customer number method is selected
    if (aia83Data.withdrawalMethod === "customerNumberPractitioners" && !aia83Data.customerNumber) {
      setIsRequiredField(true);
      return;
    }

    // Validate correspondence change customer number if needed
    if (aia83Data.correspondenceChange.changeAddress && 
        aia83Data.correspondenceChange.method === "customerNumber" && 
        !aia83Data.correspondenceChange.customerNumber) {
      setIsRequiredField(true);
      return;
    }

    // Validate signature info
    if (!aia83Data.signatoryInfo.name || !aia83Data.signatoryInfo.registrationNumber) {
      setIsRequiredField(true);
      return;
    }

    setIsRequiredField(false);

    // Don't override application_nums if already set from chatbot
    const finalApplicationNums = aia83Data.application_nums || applicationNumbers || "";
    const dataToSubmit = {
      ...aia83Data,
      application_nums: finalApplicationNums
    };

    const formattedString = JSON.stringify(dataToSubmit)

    const message = `Please prepare AIA-83 Request for Withdrawal as Attorney or Agent form with the following details:\n\n${formattedString}`;

    handleModuleSubmit({textInput: "AIA 83 Form Data", message, originatingRequestId, requestId, largeSlotData: formattedString});
    setAia83Intent(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setAia83Data((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setAia83Data((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleNestedInputChange = (section, field, value) => {
    setAia83Data((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleChecklistChange = (field, checked) => {
    setAia83Data((prev) => ({
      ...prev,
      withdrawalChecklist: {
        ...prev.withdrawalChecklist,
        [field]: checked,
      },
    }));
  };

  const handleCorrespondenceChange = (field, value) => {
    setAia83Data((prev) => ({
      ...prev,
      correspondenceChange: {
        ...prev.correspondenceChange,
        [field]: value,
      },
    }));
  };

  const handleCfrReasonChange = (section, checked) => {
    setAia83Data((prev) => ({
      ...prev,
      cfrReasons: {
        ...prev.cfrReasons,
        [section]: checked,
      },
    }));
  };

  const handleSignatoryChange = (field, value) => {
    setAia83Data((prev) => ({
      ...prev,
      signatoryInfo: {
        ...prev.signatoryInfo,
        [field]: value,
      },
    }));
  };

  const handleReset = () => {
    setAia83Data({
      application_nums: applicationNumbers || "",
      withdrawalMethod: "allPractitioners",
      customerNumber: "",
      cfrReasons: {
        "_11_116_a_1": false,
        "_11_116_a_2": false,
        "_11_116_a_3": false,
        "_11_116_b_7": false,
        "_11_116_b_1": false,
        "_11_116_b_2": false,
        "_11_116_b_3": false,
        "_11_116_b_4": false,
        "_11_116_b_5": false,
        "_11_116_b_6": false
      },
      withdrawalChecklist: {
        reasonableNotice: true,
        deliveredPapers: true,
        notifiedClient: true
      },
      explanation: "",
      correspondenceChange: {
        changeAddress: false,
        method: "customerNumber",
        customerNumber: "",
        applicantAddress: {
          address: "",
          state: "",
          city: "",
          country: "",
          zipcode: "",
          telephone: "",
          email: ""
        }
      },
      signatureRequired: true,
      signatoryInfo: {
        name: "",
        registrationNumber: "",
        addressLine1: "",
        state: "",
        city: "",
        country: "",
        zipcode: "",
        telephone: "",
        email: ""
      }
    });
    setUploadedFiles([]);
    setIsRequiredField(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "92vh",
        background: "#fefefe",
        display: "flex",
        flexDirection: "column",
        padding: "1.5rem",
        gap: "1rem",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => {
            setAia83Intent(false);
            handleStop();
            const time = getCurrentTime();
            setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled.", time }]);
          }}
          sx={{
            width: "fit-content",
            ":hover": {
              background: "transparent",
            },
          }}
        >
          <Close />
        </Button>
      </Box>

      <Box>
        <Typography fontSize={"1.5rem"} fontWeight="600" color={"black"} sx={{ mb: 2, mr: 1 }}>
          REQUEST FOR WITHDRAWAL AS ATTORNEY OR AGENT AND CHANGE OF CORRESPONDENCE ADDRESS
        </Typography>
        {/* <Typography fontSize={"1rem"} color={"black"} sx={{ mt: 1 }}>
          Please choose how you want to update the Applicant Address:
        </Typography> */}
      </Box>

      <Box sx={{ height: "60vh", overflowY: "scroll", padding: "1rem" }}>

        {/* Withdrawal Method Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            Please choose how you want to update the Applicant Address:
          </Typography>
          {(isRequiredField && !aia83Data.withdrawalMethod) && (<FormHelperText error sx={{mt: 1}}>Please select how you want to update the Applicant Address</FormHelperText>)}
          <FormControl component="fieldset">
            <RadioGroup
              name="withdrawalMethod"
              value={aia83Data.withdrawalMethod}
              onChange={handleInputChange}
            >
              <FormControlLabel
                value="allPractitioners"
                control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                label="all the practitioners of record;"
              />
              <FormControlLabel
                value="specificPractitioners"
                control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                label="the practitioners (with registration numbers) of record listed on the attached paper(s); or"
              />
              <FormControlLabel
                value="customerNumberPractitioners"
                control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                // label="the practitioners of record associated with Customer Number:"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>the practitioners of record associated with Customer Number:</span>
                      <TextField
                        size="small"
                        // label="Customer Number"
                        name="customerNumber"
                        value={aia83Data.customerNumber}
                        onChange={handleInputChange}
                        error={isRequiredField && aia83Data.withdrawalMethod === "customerNumberPractitioners" && !aia83Data.customerNumber}
                        required={aia83Data.withdrawalMethod === "customerNumberPractitioners"}
                        sx={{
                          width: "80px",
                          "& .MuiOutlinedInput-root": {
                            height: "32px",
                            "& fieldset": {
                              borderColor: "#ccc",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                        placeholder="___"
                      />
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {aia83Data.withdrawalMethod === "customerNumberPractitioners" && (
            <Box sx={{ mt: 2}}>

              {/* 37 CFR Withdrawal Reasons */}
              <Box sx={{ mt: 2 }}>
                <Typography fontSize={"1rem"} sx={{ mb: 2 }}>
                  NOTE: The immediately preceding box should only be marked when the practitioners were appointed using the listed
                  Customer Number.
                </Typography>
                <Typography fontSize={"1rem"} fontWeight="600" sx={{ mb: 2 }}>
                  The reason(s) for this request are those described in 37 CFR:
                </Typography>
                
                {/* First row of checkboxes */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_a_1"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_a_1", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(a)(1)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_a_2"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_a_2", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(a)(2)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_a_3"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_a_3", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(a)(3)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_1"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_1", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(1)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                </Box>

                {/* Second row of checkboxes */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>     
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_2"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_2", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(2)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_3"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_3", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(3)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_4"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_4", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(4)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_5"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_5", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(5)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                </Box>

                {/* Third row - remaining checkboxes */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_6"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_6", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(6)"
                    sx={{ fontSize: "0.9rem" }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={aia83Data.cfrReasons["_11_116_b_7"]}
                        onChange={(e) => handleCfrReasonChange("_11_116_b_7", e.target.checked)}
                        sx={{ "&.Mui-checked": { color: "#005cb9" } }}
                      />
                    }
                    label="11.116(b)(7) with Notes"
                    sx={{ fontSize: "0.9rem" }}
                  />
                </Box>
                {aia83Data.cfrReasons["_11_116_b_7"] && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Notes"
                      multiline
                      rows={4}
                      variant="outlined"
                      fullWidth
                      value={aia83Data.cfrReasons["_11_116_b_7_notes"]}
                      onChange={(e) => handleCfrReasonChange("_11_116_b_7_notes", e.target.value)}
                    />
                  </Box>
                )}
                
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Withdrawal Requirements Checklist */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            Check each box below that is factually correct. WARNING: If a box is left unchecked, the request will likely not be approved.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={aia83Data.withdrawalChecklist.reasonableNotice}
                  onChange={(e) => handleChecklistChange("reasonableNotice", e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#005cb9",
                    },
                  }}
                />
              }
              label="I/we have given reasonable notice to the client, prior to the expiration of the response period, that the practitioner(s) intend to withdraw from employment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aia83Data.withdrawalChecklist.deliveredPapers}
                  onChange={(e) => handleChecklistChange("deliveredPapers", e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#005cb9",
                    },
                  }}
                />
              }
              label="I/we have delivered to the client or a duly authorized representative of the client all papers and property (including funds) to which the client is entitled"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={aia83Data.withdrawalChecklist.notifiedClient}
                  onChange={(e) => handleChecklistChange("notifiedClient", e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#005cb9",
                    },
                  }}
                />
              }
              label="I/we have notified the client of any responses that may be due and the time frame within which the client must respond."
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Explanation Section */}
        <Box sx={{ mb: 3 }}>
          <TextField
            name="explanation"
            value={aia83Data.explanation}
            onChange={handleInputChange}
            multiline
            rows={3}
            placeholder="Please provide an explanation, if necessary:"
            fullWidth
            variant="outlined"
            sx={{
              "& label.Mui-focused": {
                color: "#005cb9",
              },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#005cb9",
                },
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 3, borderColor: "#333", borderWidth: 1.5 }} />

        {/* Correspondence Address Change */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            Complete the following section only when the correspondence address will change. Changes of address will only be accepted to an applicant. Change the correspondence address and direct all future correspondence to:
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={aia83Data.correspondenceChange.changeAddress}
                onChange={(e) => handleCorrespondenceChange("changeAddress", e.target.checked)}
                sx={{
                  "&.Mui-checked": {
                    color: "#005cb9",
                  },
                }}
              />
            }
            label="Change correspondence address"
          />

          {aia83Data.correspondenceChange.changeAddress && (
            <Box sx={{ mt: 2, pl: 2 }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  value={aia83Data.correspondenceChange.method}
                  onChange={(e) => handleCorrespondenceChange("method", e.target.value)}
                >
                  <FormControlLabel
                    value="customerNumber"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                    label="The address of the applicant associated with Customer Number:"
                  />
                  <FormControlLabel
                    value="applicant"
                    control={<Radio sx={{ "&.Mui-checked": { color: "#005cb9" } }} />}
                    label="Applicant"
                  />
                </RadioGroup>
              </FormControl>

              {aia83Data.correspondenceChange.method === "customerNumber" && (
                <TextField
                  size="small"
                  // label="Customer Number"
                  value={aia83Data.correspondenceChange.customerNumber}
                  onChange={(e) => handleCorrespondenceChange("customerNumber", e.target.value)}
                  error={isRequiredField && aia83Data.correspondenceChange.changeAddress && aia83Data.correspondenceChange.method === "customerNumber" && !aia83Data.correspondenceChange.customerNumber}
                  required={aia83Data.correspondenceChange.changeAddress && aia83Data.correspondenceChange.method === "customerNumber"}
                  sx={{
                      width: "80px",
                      "& .MuiOutlinedInput-root": {
                        height: "32px",
                        "& fieldset": {
                          borderColor: "#ccc",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#005cb9",
                        },
                      },
                    }}
                    placeholder="___"
                />
              )}

              {aia83Data.correspondenceChange.method === "applicant" && (
                <Box sx={{ mt: 2 }}>
                  <Typography fontSize={"1rem"} fontWeight="500" sx={{ mb: 2 }}>
                    Applicant Address Information:
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
                    <TextField
                      label="Address"
                      value={aia83Data.correspondenceChange.applicantAddress.address}
                      onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, address: e.target.value})}
                      fullWidth
                      sx={{
                        "& label.Mui-focused": {
                          color: "#005cb9",
                        },
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#005cb9",
                          },
                        },
                      }}
                    />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <TextField
                        label="State"
                        value={aia83Data.correspondenceChange.applicantAddress.state}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, state: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                      <TextField
                        label="City"
                        value={aia83Data.correspondenceChange.applicantAddress.city}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, city: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <TextField
                        label="Country"
                        value={aia83Data.correspondenceChange.applicantAddress.country}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, country: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                      <TextField
                        label="Zipcode"
                        value={aia83Data.correspondenceChange.applicantAddress.zipcode}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, zipcode: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <TextField
                        label="Telephone Number"
                        value={aia83Data.correspondenceChange.applicantAddress.telephone}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, telephone: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                      <TextField
                        label="Email"
                        value={aia83Data.correspondenceChange.applicantAddress.email}
                        onChange={(e) => handleCorrespondenceChange("applicantAddress", {...aia83Data.correspondenceChange.applicantAddress, email: e.target.value})}
                        sx={{
                          "& label.Mui-focused": {
                            color: "#005cb9",
                          },
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "#005cb9",
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Signature Section */}
        <Box sx={{ mb: 3 }}>
          <Typography fontSize={"1.1rem"} fontWeight="600" sx={{ mb: 2 }}>
            I am authorized to sign on behalf of myself and all withdrawing practitioners
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={aia83Data.signatureRequired}
                onChange={handleInputChange}
                name="signatureRequired"
                sx={{
                  "&.Mui-checked": {
                    color: "#005cb9",
                  },
                }}
              />
            }
            label="Include signature information"
          />

            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
              <TextField
                label="Name"
                value={aia83Data.signatoryInfo.name}
                onChange={(e) => handleSignatoryChange("name", e.target.value)}
                error={isRequiredField && !aia83Data.signatoryInfo.name}
                required
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Registration Number"
                value={aia83Data.signatoryInfo.registrationNumber}
                required
                error={isRequiredField && !aia83Data.signatoryInfo.registrationNumber}
                onChange={(e) => handleSignatoryChange("registrationNumber", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Address Line 1"
                value={aia83Data.signatoryInfo.addressLine1}
                onChange={(e) => handleSignatoryChange("addressLine1", e.target.value)}
                sx={{
                  gridColumn: "span 2",
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="State"
                value={aia83Data.signatoryInfo.state}
                onChange={(e) => handleSignatoryChange("state", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="City"
                value={aia83Data.signatoryInfo.city}
                onChange={(e) => handleSignatoryChange("city", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Country"
                value={aia83Data.signatoryInfo.country}
                onChange={(e) => handleSignatoryChange("country", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Zipcode"
                value={aia83Data.signatoryInfo.zipcode}
                onChange={(e) => handleSignatoryChange("zipcode", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Telephone Number"
                value={aia83Data.signatoryInfo.telephone}
                onChange={(e) => handleSignatoryChange("telephone", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
              <TextField
                label="Email"
                value={aia83Data.signatoryInfo.email}
                onChange={(e) => handleSignatoryChange("email", e.target.value)}
                sx={{
                  "& label.Mui-focused": {
                    color: "#005cb9",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#005cb9",
                    },
                  },
                }}
              />
            </Box>

          <Box sx={{ mt: 3, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
            <Typography fontSize={"0.9rem"} sx={{ color: "#333", lineHeight: 1.5 }}>
              <strong>NOTE:</strong> Withdrawal is effective when approved rather than when received.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleReset}
        >
          Reset
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={aia83Data.signatoryInfo.name.trim() === "" || aia83Data.signatoryInfo.registrationNumber.trim() === "" || (aia83Data.withdrawalMethod === "customerNumberPractitioners" && aia83Data.customerNumber.trim() === "") || (aia83Data.correspondenceChange.changeAddress && aia83Data.correspondenceChange.method === "customerNumber" && aia83Data.correspondenceChange.customerNumber.trim() === "")}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

AIA83Modal.propTypes = {
  setAia83Intent: PropTypes.func,
  originatingRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleModuleSubmit: PropTypes.func,
  handleStop: PropTypes.func,
  setMessages: PropTypes.func,
  applicationNumbers: PropTypes.string
};

export default AIA83Modal;
